import React, { useEffect, useState } from 'react';
import { Avatar, Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DisplayValue } from '../../devices/shared-ui';
import DeviceLoadingSkeleton from '../../devices/shared-ui/deviceLoadingSkeleton';
import {
  DeviceInfo,
  DeviceInformation,
} from '../../devices/shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../../devices/shared-ui/alerts-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useDeviceHasAlerts } from '../../../system/AlertsManager';
import moment from 'moment';
import {
  EnergySupplierSolarSystemDetailQuery,
  useEnergySupplierSolarSystemDetailQuery,
  useEnergySupplierSolarSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { formatDeviceMetric } from '../../../utils/formatters';
import { SolarManagementSystemComponent } from '../../devices/virtual-power-plant/solar-management-system';
import { EnergyControllerComponent } from '../../devices/virtual-power-plant/energy-controller';
import { EnergyInverterComponent } from '../../devices/virtual-power-plant/energy-inverter';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const SolarSystemIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L14.5 8.5L21 11L14.5 13.5L12 20L9.5 13.5L3 11L9.5 8.5L12 2Z"
      fill="currentColor"
    />
    <path
      d="M12 4L13.5 8L17 9.5L13.5 11L12 15L10.5 11L7 9.5L10.5 8L12 4Z"
      fill="currentColor"
      fillOpacity="0.5"
    />
    <path
      d="M12 6L12.5 7.5L14 8.5L12.5 9.5L12 11L11.5 9.5L10 8.5L11.5 7.5L12 6Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
  </svg>
);

export function EnergySupplierSolarSystemComponent({
  participantId,
}: {
  participantId: string;
}) {
  const { data, loading, error } = useEnergySupplierSolarSystemDetailQuery({
    variables: { id: participantId },
  });

  const [participant, setParticipant] =
    useState<
      Partial<
        EnergySupplierSolarSystemDetailQuery['energySupplierSolarSystemById']
      >
    >();
  const [expanded, setExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  // const hasAlerts = useDevicesHaveAlerts({
  //   deviceIds: [smsId, controllerId, inverterId],
  // });

  const hasAlerts = false; // TODO: Peter: change to devicesHaveAlerts and pass in dependent device id array.
  const isConnected = participant?.connected?.value ?? false;

  useEnergySupplierSolarSystemDetailUpdatesSubscription({
    variables: { id: participantId },
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energySupplierSolarSystemById) {
      setParticipant(data.energySupplierSolarSystemById);
    }
  }, [data]);

  useEffect(() => {
    if (participant) {
      const panelInfo: DeviceInformation = {
        'Power State': (participant.powerState?.value ?? 'Unknown').toString(),
        'Connection Status': (
          participant.connected?.value ?? 'Unknown'
        ).toString(),
        'Last Update': participant.timestamp
          ? moment(participant.timestamp).fromNow()
          : 'Unknown',
      };

      const systemInfo: DeviceInformation = {
        'Maximum Power Output': formatDeviceMetric({
          value: participant.maximumPowerOutput,
          units: 'W',
        }),
        'Inverter ID': participant.inverterId ?? 'Not assigned',
        'Controller ID': participant.controllerId ?? 'Not assigned',
      };

      const solarInfo: DeviceInformation = {
        'Tilt Angle': formatDeviceMetric({
          value: participant.tiltAngle?.value,
          units: participant.tiltAngle?.units ?? '°',
        }),
        Azimuth: formatDeviceMetric({
          value: participant.azimuth?.value,
          units: participant.azimuth?.units ?? '°',
        }),
      };

      const devicesInfo: DeviceInformation = {
        'Device ID': participant._id ?? 'Unidentified',
        Name: participant.name ?? 'Unnamed',
        Location: participant.location ?? 'Unknown',
        Status: (participant.updateStatus ?? 'Unknown').toString(),
        'Last Updated': participant.timestamp
          ? moment(participant.timestamp).fromNow()
          : 'Unknown',
      };

      setDeviceInfo([panelInfo, systemInfo, solarInfo, devicesInfo]);
    }
  }, [participant]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return error ? (
    <div>Error</div>
  ) : loading ? (
    <DeviceLoadingSkeleton />
  ) : participant ? (
    <Card
      sx={{
        backgroundColor: hasAlerts
          ? '#ffd5d5'
          : isConnected
            ? 'rgba(0, 255, 0, 0.05)'
            : '#ebf7f6',
        boxShadow: 1,
        p: 0,
        width: '100%',
        border: hasAlerts ? '1px solid red' : '',
        borderRadius: '4px',
        marginTop: '4px',
      }}
    >
      <CardHeader
        sx={{
          paddingTop: '0px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '0px',
          border: '1px solid rgba(0,0,0, 0.1)',
        }}
        title={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ padding: '0px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingTop: '6px',
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      paddingTop: '3px',
                    }}
                  >
                    {participant?.name}
                  </Typography>
                </div>
              </Box>
            </div>
            <div style={{ flex: '1 1 auto', flexDirection: 'column' }} />
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ p: 0 }}>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <div style={{ marginLeft: '6px' }}>&nbsp;</div>
                  <DisplayValue
                    value={participant.maximumPowerOutput}
                    units="W"
                    size={'h3'}
                    unitsSize={'14px'}
                    style={{
                      paddingTop: '8px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    }}
                  />
                  {participant.smsId ? (
                    <SolarManagementSystemComponent id={participant.smsId} />
                  ) : null}
                  {participant.controllerId ? (
                    <EnergyControllerComponent id={participant.controllerId} />
                  ) : null}
                  {participant.inverterId ? (
                    <EnergyInverterComponent id={participant.inverterId} />
                  ) : null}

                  <div style={{ marginRight: '6px' }}>&nbsp;</div>
                </Stack>
              </Box>
            </div>
          </div>
        }
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              padding: '0px',
              margin: '0px',
            }}
          >
            <SolarSystemIcon />
          </Avatar>
        }
        action={
          <Stack direction="row" spacing={1}>
            <ToggleAlertDetailsButton
              expand={alertsExpanded}
              onClick={() => setAlertsExpanded(!alertsExpanded)}
              device={{}}
              // participant // TODO: Peter: incorporate any internal devices into this
              buttonLabel="Alerts"
            />
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </Stack>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceInfo infoEntries={deviceInfo} />
        </Box>
      </Collapse>
      <Collapse in={alertsExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceAlerts
            device={{}}
            // participant: // TODO: Peter: incorporate internal devices here.
            alertsExpanded={alertsExpanded}
          />
        </Box>
      </Collapse>
    </Card>
  ) : null;
}
